/**
 * What the coach says. Every feedback type has several phrasings (6-8 for the frequent ones, at
 * least 4 for the rest), picked without repeating recent ones (see PhrasePicker), in two age
 * bands: 5-7 (short, concrete, playful) and 8-12 (plainer, more technical words such as "tongue
 * tip" and "molars"). Placeholders: {word} the practice word or syllable, {heard} what the attempt
 * sounded like (e.g. "wed"), {sound} the sound label.
 *
 * Praise names what was heard (a clear /r/), never a movement the app cannot observe, except
 * after a cue, when it credits the cued technique. Placement cues live in cues.ts, each with the
 * diagram shown while it is spoken. See docs/research/r-practice-feedback.md,
 * docs/research/r-advice-and-diagrams.md and docs/research/progress-feedback-turn-taking.md.
 */

export type AgeBand = 'younger' | 'older';

export type PhraseKey =
  | 'prompt'
  | 'promptAgain'
  | 'correctFirst'
  | 'correctCued'
  /** A near miss counted while the criterion is eased (shaping): honest, not "perfect". */
  | 'correctApprox'
  | 'correctBrief'
  | 'closer'
  | 'notYet'
  | 'notYetBrief'
  | 'nearMiss'
  | 'modelInvite'
  | 'yourTurn'
  | 'playbackIntro'
  | 'contrastGlide'
  | 'contrastLateral'
  | 'contrastDistortion'
  | 'simplify'
  | 'syllableCorrect'
  | 'backToWord'
  | 'moveOnProgress'
  | 'moveOnProgressReturn'
  | 'moveOnReturn'
  | 'moveOn'
  | 'requeue'
  | 'easyWin'
  /** The child asked for help ("show me how"). */
  | 'helpIntro'
  /** Before the first mouth picture of a session: what it shows (with a crossed-out one: Pair). */
  | 'hintIntro'
  | 'hintIntroPair'
  /** Self-check after playback: which word did it sound like? Then agree or disagree. */
  | 'selfCheck'
  | 'selfCheckAgree'
  | 'selfCheckDisagree'
  /** The child picks the next word (two pictures, both said after the line). */
  | 'chooseNext'
  /** Riddles (exercises.ts): solved, answered with another word, or no answer. */
  | 'riddleSolved'
  | 'riddleReveal'
  | 'riddleHelp'
  | 'silence'
  | 'silenceModel'
  | 'micPause'
  | 'notHeard'
  | 'tooQuiet'
  | 'tooLoud'
  | 'tooLong'
  | 'offTarget'
  | 'sessionEnd'
  /**
   * Story (docs/design/story.md), told by the coach in the flow: the first mission ever opens with
   * the backstory (content/story.ts); every later mission with a short briefing. "Foe" is
   * explained in passing for 5-7 year olds, then woven into reminders after correct words.
   */
  | 'missionStart'
  /** The mission's first word, after the briefing: listen, then say it back. */
  | 'firstPrompt'
  | 'foeReminder';

type Bank = Record<PhraseKey, readonly string[]>;

const younger: Bank = {
  prompt: [
    'Say {word}.',
    'Your turn: {word}.',
    'Can you say {word}?',
    'Let me hear {word}.',
    'Ready? Say {word}.',
    'Scanner on! Say {word}.',
    'Next word: {word}.',
  ],
  promptAgain: [
    'Try {word} again.',
    'Again: {word}.',
    'Let’s hear {word} again.',
    'Again, {word}.',
    'Give it another try: {word}.',
    'Say {word} again.',
  ],
  correctFirst: [
    'Yes! That was a strong {sound} in {word}!',
    'You did it! I heard a clear {sound}.',
    'That’s the one! A real {sound} sound.',
    'Yes! Your tongue made a great {sound}.',
    'Yes! {word}, with a great {sound}.',
    'Super! That {sound} came through loud and clear.',
    'Yes! That {sound} was right on target.',
    'Woohoo! A clear {sound} in {word}.',
  ],
  correctCued: [
    'Yes! Keeping your tongue like that really worked.',
    'That’s it! Your tongue knew just what to do.',
    'You did it! That trick worked for your {sound}.',
    'Yes! Doing it that way made a great {sound}.',
    'There it is! Your hard work paid off.',
    'Great listening! You made the {sound} just like we practised.',
  ],
  correctApprox: [
    'That was much closer, so it counts! Your {sound} is getting stronger.',
    'Closer and closer! That one counts.',
    'More {sound} that time. That one counts!',
    'That’s really close now. It counts! Keep practising that {sound}.',
  ],
  correctBrief: [
    'Yes!',
    'Got it!',
    'Nice!',
    'That’s right!',
    'Good one!',
    'Correct!',
    'Yep!',
    'Great!',
  ],
  closer: [
    'That was closer! Do it again, just like that.',
    'Ooh, better! Your {sound} is getting stronger.',
    'Closer! Keep doing what you just did.',
    'I heard more {sound} that time. Again!',
    'Getting warmer! Again, just like that.',
    'Much better! Say it again.',
  ],
  // About the sound and the next step: no praise for effort the child did not feel they made ("Why
  // do I get thanked for trying when I barely did anything", QA 2026-10-11) and no generic failure
  // words (coaching-framework.md).
  notYet: [
    'Let’s listen to the {sound} again.',
    'Let’s work on that {sound} together.',
    'Let’s practise that {sound} a bit more.',
    'The {sound} needs a little more work.',
    'Let’s try the {sound} again.',
    'Let’s keep working on that {sound}.',
  ],
  notYetBrief: [
    'Again.',
    'Try it again.',
    'Keep going.',
    'Another try.',
    'Let’s try again.',
    'Go again.',
  ],
  nearMiss: [
    'So close! Let’s try a little stronger.',
    'Ooh, nearly! Try it again.',
    'Nearly! Again, with more {sound}.',
    'Almost there! Again.',
    'Nearly had it!',
    'Close! Try a bit more {sound}.',
  ],
  modelInvite: [
    'Listen to me: {word}.',
    'Listen: {word}.',
    'Hear how I say it: {word}.',
    'Listen closely: {word}.',
    'I’ll say it first: {word}.',
    'Listen to my {sound}: {word}.',
  ],
  yourTurn: [
    'Your turn.',
    'Now you.',
    'Now you try.',
    'You say it.',
    'Copy me.',
    'Now you say it.',
  ],
  playbackIntro: [
    'Here’s what I heard:',
    'Listen to yours:',
    'This is how yours sounded:',
    'I heard this:',
    'Let’s listen to yours:',
  ],
  contrastGlide: [
    'That sounded like {heard}. I’m listening for {word}.',
    'I heard {heard}, with a w sound. Listen for the {sound}: {word}.',
    'That was {heard}. Let’s make it {word}.',
    'Yours sounded like {heard}. Mine is {word}.',
    'I heard a w in {heard}. Now listen to the {sound}: {word}.',
  ],
  contrastLateral: [
    'That sounded like {heard}. I’m listening for {word}.',
    'I heard {heard}, with an l sound. Listen for the {sound}: {word}.',
    'That was {heard}. Let’s turn it into {word}.',
    'Yours sounded like {heard}. Mine is {word}.',
  ],
  contrastDistortion: [
    'Let’s make the {sound} stronger. Listen to mine: {word}.',
    'Listen for a strong {sound}: {word}.',
    'Hear the {sound} in mine? {word}.',
    'Let’s make that {sound} growl. Listen: {word}.',
  ],
  // The easier target is a syllable with the same r (rah for rock), not a part of the word.
  simplify: [
    'Let’s practise the {sound} on its own first: {word}.',
    'Let’s break it down. First just {word}.',
    'Let’s warm up the {sound} with {word}.',
    'First, just the start: {word}.',
    'Let’s build it up, starting with {word}.',
  ],
  syllableCorrect: [
    'Yes! That {word} had a great {sound}!',
    'There it is! A real {sound} in {word}.',
    'You got it! Strong {sound}.',
    'That’s the sound! Great {sound}.',
  ],
  backToWord: [
    'Now put that {sound} into {word}.',
    'Now let’s try it in the word: {word}.',
    'Use that same {sound} for {word}.',
    'Keep that {sound} and say {word}.',
  ],
  moveOnProgress: [
    'Great practising! Your {sound} is improving. Let’s try another word.',
    'Your {sound} is getting better! Time for a new word.',
    'That’s real progress! Let’s try a new word now.',
    'You’re getting closer every time! Here’s a new word.',
  ],
  moveOnProgressReturn: [
    'You’re getting closer every time! Let’s try a new word and come back to this one.',
    'That’s real progress! We’ll come back to this word later.',
    'Your {sound} is getting better! We’ll try this word again later.',
    'Great practising! New word now, and this one comes back later.',
  ],
  moveOn: [
    'Good effort! Let’s try another word.',
    'Thanks for working so hard on that one. Let’s switch words.',
    'That one takes practice! Here’s a different word.',
    'Nice work sticking with it. Time for a new word.',
  ],
  moveOnReturn: [
    'That one takes practice! Let’s try a different word and come back later.',
    'We’ll come back to that one. Here’s a new word.',
    'Thanks for working so hard on that one. We’ll try it again later.',
    'Good effort! New word now, and that one comes back later.',
  ],
  requeue: [
    'Remember this one? Let’s try {word} again.',
    'Here’s {word} again. Let’s see how it goes now.',
    '{word} is back! Give it another try.',
    'Let’s try {word} again.',
  ],
  riddleSolved: [
    'You solved the riddle!',
    'Riddle solved!',
    'You figured it out!',
    'That’s the answer!',
  ],
  riddleReveal: [
    'Nice guess! The answer is {word}.',
    'Good thinking! This one is {word}.',
    'Clever guess! It’s {word}.',
    'Good try! The answer is {word}.',
  ],
  riddleHelp: [
    'Let’s solve it together. It’s {word}.',
    'Here’s the answer: {word}.',
    'Let me tell you. It’s {word}.',
    'The answer is {word}.',
  ],
  chooseNext: [
    'You choose! Which one next?',
    'Your pick! Which one do you want?',
    'Pick the next one and say it:',
    'Which one shall we do next? You choose:',
  ],
  selfCheck: [
    'Did that sound like {word}, or {heard}?',
    'Was that {word}, or {heard}? Tap the one you heard.',
    'Which did you hear: {word}, or {heard}?',
    'Detective ears! {word}, or {heard}?',
  ],
  selfCheckAgree: [
    'You heard it too. That was {heard}. Now listen for the {sound}:',
    'Good listening! It sounded like {heard}. Here’s the {sound}:',
    'Yes, I heard {heard} too. Listen to mine:',
    'Nice listening! That was {heard}. Now the {sound}:',
  ],
  selfCheckDisagree: [
    'Hmm, I heard {heard}. Listen again: yours, then mine.',
    'I heard {heard}. Let’s listen to both again.',
    'To me it sounded like {heard}. Listen to yours and mine.',
    'I heard a {heard}. Listen closely: yours, then mine.',
  ],
  hintIntro: [
    'Look at the picture. It shows the inside of a mouth, from the side. The green tick shows how to make the {sound}.',
    'See the picture? It’s a mouth, seen from the side. Look at the tongue: the green tick shows the {sound}.',
  ],
  hintIntroPair: [
    'Look at the pictures. They show the inside of a mouth, from the side. The red cross is the sound I heard, and the green tick is the {sound}. Look at the tongue.',
    'See the two pictures? They’re mouths, seen from the side. The red cross is what I heard. The green tick is the {sound}. Watch how the tongue moves.',
  ],
  helpIntro: [
    'Here’s how to make an {sound}.',
    'Let me show you a trick.',
    'Here’s a way to try it.',
    'Good idea to ask! Try it like this.',
  ],
  easyWin: [
    'Let’s try {word} now.',
    'Let’s say {word} together.',
    'Here’s {word} again.',
    'Back to {word}. Your turn.',
  ],
  silence: [
    'I’m listening. Say {word}.',
    'Whenever you’re ready, say {word}.',
    'Your turn: {word}.',
    'Go ahead and say {word}.',
    'Ready when you are: {word}.',
    'The scanner is waiting for {word}.',
  ],
  silenceModel: [
    'Listen first: {word}. Now you.',
    'I’ll say it first: {word}. Your turn.',
    'Let’s say it together. {word}.',
    'Here it is: {word}. Now you try.',
  ],
  micPause: [
    'I’ll wait here. Tap the microphone when you’re ready.',
    'Let’s take a break. Tap the microphone when you want to play again.',
    'Scanner on standby. Tap the microphone to keep going.',
    'I’ll pause the scanner. Tap the microphone to start again.',
  ],
  notHeard: [
    'I couldn’t quite hear that. Say {word} again?',
    'Oops, I missed that. Say {word} again?',
    'I didn’t catch that. Can you say {word} again?',
    'The scanner missed that one. Say {word}?',
  ],
  tooQuiet: [
    'Can you say it a bit louder? {word}.',
    'Try a little closer to the screen, and say {word}.',
    'A bit louder, please: {word}.',
    'Use your strong voice: {word}.',
  ],
  tooLoud: [
    'Your normal voice works best.',
    'Use your everyday voice, it works best.',
    'Nice and easy, your normal voice is perfect.',
    'A little quieter works better for the scanner.',
  ],
  tooLong: [
    'One word, please: {word}.',
    'Just {word} this time.',
    'Only the word, please: {word}.',
    'Just the word, please: {word}.',
  ],
  offTarget: [
    'I heard a different word. Let’s say {word}.',
    'I was listening for {word}. Your turn.',
    'Let’s say {word} this time.',
    'I’m listening for {word}. Give it a try.',
  ],
  sessionEnd: [
    'Great practising today! Your {sound} is getting stronger.',
    'Mission complete! You worked really hard on your {sound}.',
    'What a great session! See you next time, agent.',
    'Awesome work today! Your {sound} practice is paying off.',
  ],
  firstPrompt: [
    '[mysterious] Okay, agent, get ready. Here comes the first word. Listen closely, then say it back with a strong r: {word}.',
    '[mysterious] Shh, the scanner is warming up. Your first word is… {word}. Now you say it, with a strong r!',
  ],
  missionStart: [
    'A foe is hiding on this floor. Let’s find its name!',
    'Somebody is playing tricks up here. Let’s unmask them!',
    'Shh! A trickster in disguise is close by. Strong r’s will find its name.',
    'New floor, new foe! Let’s get its codename.',
  ],
  foeReminder: [
    'Every strong r gets us closer to that sneaky foe in disguise!',
    'That trickster can’t hide much longer!',
    'Look, the foe’s secret name is showing up, letter by letter!',
    'Our sneaky foe is getting nervous!',
  ],
};

const older: Bank = {
  ...younger,
  hintIntro: [
    'Check the picture: a side view inside the mouth. The green tick marks the tongue position for {sound}.',
    'The picture shows a side view of the mouth. The tick marks where your tongue goes for {sound}.',
  ],
  hintIntroPair: [
    'Compare the two side views of the mouth. The cross is what I heard; the tick is the {sound}. Watch the tongue.',
    'Two side views of the mouth: the cross is the sound I heard, the tick is the {sound}. Look at what the tongue does differently.',
  ],
  prompt: [
    'Say {word}.',
    'Next: {word}.',
    'Your turn: {word}.',
    'Go ahead: {word}.',
    'Let’s hear {word}.',
    'Scanner ready. {word}.',
  ],
  correctFirst: [
    'Yes! That was a clear {sound} in {word}.',
    'Spot on. Your {sound} was correct.',
    'That’s it: a strong {sound}.',
    'Nailed it. Clear {sound}.',
    'Correct! {word} with a good {sound}.',
    'Good one, that {sound} was right.',
    'Clean {sound}. Well done.',
  ],
  correctApprox: [
    'Close enough to count. Your {sound} is getting there.',
    'That one counts: much closer. Keep shaping it.',
    'Nearly there, so it counts. Keep that tongue position.',
    'Good progress. That {sound} was close enough to count.',
  ],
  correctCued: [
    'Yes! That tongue position worked.',
    'That’s it. Bracing your tongue like that made a correct {sound}.',
    'Correct! Remember how that felt.',
    'There it is. That placement works for you.',
    'Correct. Keep using that tongue position.',
  ],
  closer: [
    'That was closer. Do exactly that again.',
    'Better! More {sound} that time.',
    'Closer. Keep that tongue position.',
    'Improving. Again, like that.',
    'Nearly there, that one had more {sound}.',
    'Good improvement. Repeat it.',
  ],
  notYet: [
    'Focus on the {sound}.',
    'Listen to the {sound} again.',
    'Let’s refine that {sound}.',
    'The {sound} needs adjusting.',
    'The {sound} takes practice. Again.',
    'Let’s work on that {sound}.',
  ],
  modelInvite: [
    'Listen: {word}.',
    'Here’s how it sounds: {word}.',
    'Listen to the {sound}: {word}.',
    'Model first: {word}.',
    'Hear mine: {word}.',
    'Pay attention to the start: {word}.',
  ],
  sessionEnd: [
    'Good session. Your {sound} is getting more consistent.',
    'Mission complete. Solid work on your {sound} today.',
    'That’s a wrap, agent. Great practice.',
    'Nice work today. Keep it up next time.',
  ],
  firstPrompt: [
    '[mysterious] Scanner’s live. First word: listen, then say it back with a strong r. {word}.',
    '[mysterious] Alright, agent. Here’s your first word. Repeat after me, with a strong r: {word}.',
  ],
  missionStart: [
    'A foe is hiding on this floor. Let’s uncover its codename.',
    'Another foe, another disguise. Strong r’s will crack it.',
    'Intel says a foe is operating on this floor. Time to find its name.',
    'This foe thinks the disguise is perfect. Let’s see about that.',
  ],
  foeReminder: [
    'The codename is coming together.',
    'That foe can’t hide much longer.',
    'Every clear r gives us another letter.',
    'Keep going. The disguise is slipping.',
  ],
};

export const PHRASES: Record<AgeBand, Bank> = { younger, older };

export function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) => vars[name] ?? '');
}

/**
 * Picks phrasings without repeating: each key cycles through a shuffled order of its variants,
 * and a new cycle never starts with the variant that ended the previous one.
 */
export class PhrasePicker {
  private readonly bags = new Map<string, number[]>();
  private readonly last = new Map<string, number>();
  private readonly random: () => number;

  constructor(random: () => number = Math.random) {
    this.random = random;
  }

  pick(options: readonly string[], key: string): string {
    if (options.length === 0) return '';
    let bag = this.bags.get(key);
    if (!bag || bag.length === 0) {
      bag = options.map((_, i) => i);
      for (let i = bag.length - 1; i > 0; i--) {
        const j = Math.floor(this.random() * (i + 1));
        [bag[i], bag[j]] = [bag[j] ?? 0, bag[i] ?? 0];
      }
      if (bag.length > 1 && bag[bag.length - 1] === this.last.get(key)) bag.reverse();
      this.bags.set(key, bag);
    }
    const index = bag.pop() ?? 0;
    this.last.set(key, index);
    return options[index] ?? '';
  }
}
